import { NavigateFunction } from 'react-router'
import { setGrouping } from './filter'

export const FILTER_QUERYSTRING_PARAM = 'filter'
export const GROUPING_QUERYSTRING_PARAM = 'groupByFilter'

export function parseExistingFilters(filterString: string): Record<string, string> {
  const filterCategories: Record<string, string> = {}

  if (filterString) {
    filterString.split(';').forEach((pair) => {
      const [cat, val] = pair.split('=')
      if (cat && val) filterCategories[cat] = val
    })
  }

  return filterCategories
}

// Converts urlFilterByCategory to a URL string
export function getFilterString(urlFilterByCategory: Record<string, string>): string | undefined {
  if (Object.keys(urlFilterByCategory).length === 0) {
    return undefined
  }

  return Object.entries(urlFilterByCategory)
    .map(([cat, val]) => `${cat}=${val}`)
    .join(';')
}

// Removes the filter from the URL
export function removeFilterFromUrl(navigate: NavigateFunction): void {
  const urlParams = new URLSearchParams(window.location.search)
  urlParams.delete(FILTER_QUERYSTRING_PARAM)
  const queryString = urlParams.toString()
  const newUrl = queryString
    ? `${window.location.pathname}?${queryString}${window.location.hash}`
    : `${window.location.pathname}${window.location.hash}`
  navigate(newUrl, { replace: true })
}

export function removeFilterFromUrlWithoutNavigateParam(): void {
  const urlParams = new URLSearchParams(window.location.search)
  urlParams.delete(FILTER_QUERYSTRING_PARAM)
  urlParams.delete(GROUPING_QUERYSTRING_PARAM)
  const queryString = urlParams.toString()
  const newUrl = queryString
    ? `${window.location.pathname}?${queryString}${window.location.hash}`
    : `${window.location.pathname}${window.location.hash}`

  window.history.replaceState({}, '', newUrl)
}

// Parses a filter string from the URL, categories are separated by ';' and values by ',', e.g. "grid=nordvästra;subgrid=AKA,BRI"
export function parseFilterFromUrlString(filterString: string): Record<string, string[]> {
  const result: Record<string, string[]> = {}

  filterString.split(';').forEach((pair) => {
    const [category, valueString] = pair.split('=')
    if (category && valueString) {
      const values = valueString.split(',').map((v) => decodeURIComponent(v))
      result[category] = values
    }
  })
  return result
}

/**
 * Base64, but safe to sit in a query string unencoded.
 *
 * `btoa` emits the standard alphabet, which includes `+`. In a query string `+`
 * denotes a space, so `URLSearchParams.get()` returns the blob with every `+`
 * replaced by a space and `atob` then throws on it — the filter silently fails to
 * apply. Every in-app writer goes through `URLSearchParams.set()`, which
 * percent-encodes `+`, so this only bites a URL that arrived without that
 * encoding: hand-edited, copy-pasted, or built by another system. The chance a
 * blob contains at least one `+` grows with its length, so any non-trivial filter
 * is affected.
 *
 * Writes use `-` and `_`, which need no encoding. Padding is dropped; reads
 * restore it.
 */
export function toUrlSafeBase64(standardBase64: string): string {
  return standardBase64.replaceAll('+', '-').replaceAll('/', '_').replace(/=+$/, '')
}

/**
 * Undoes the one thing a query string does to a base64 blob.
 *
 * `URLSearchParams.get()` decodes an unencoded `+` as a space, and no base64
 * alphabet contains a space, so the reverse mapping is unambiguous. Sole owner of
 * this rule: both decoding and canonicalization go through here, because a blob
 * that decodes but does not canonicalize the same way splits the parse cache and
 * fails the saved-filter comparison.
 */
function repairQueryStringDamage(blob: string): string {
  return blob.replaceAll(' ', '+')
}

/**
 * Canonical form of a filter blob, for comparing two spellings of the same bytes.
 *
 * Bookmarked links and stored saved filters carry the standard alphabet
 * indefinitely, so the same filter can reach us spelled either way. Anything that
 * compares blobs as raw strings has to canonicalize first or it will read a
 * legacy-alphabet saved filter as different from the identical current one.
 */
export function normalizeFilterBlob(blob: string): string {
  return toUrlSafeBase64(repairQueryStringDamage(blob))
}

/**
 * Canonical form of a whole filter query string — the blob plus any grouping
 * segment, as the saved-filter dropdown stores and compares it.
 *
 * Only the leading segment is base64; the grouping segment after `&` is plain text
 * and must not be rewritten, or two different groupings could canonicalize to the
 * same string.
 */
export function normalizeFilterQueryString(value: string): string {
  const [filterBlob, ...groupingSegments] = value.split('&')

  // No `&` means there is no filter param and the whole value is the grouping — the
  // producer always appends a grouping segment when a filter is present. Canonicalizing
  // it would run plain text through the base64 mapping.
  if (groupingSegments.length === 0) {
    return value
  }

  return [normalizeFilterBlob(filterBlob), ...groupingSegments].join('&')
}

/**
 * Decodes a filter blob written in either alphabet, with or without padding.
 *
 * Throws on input that is genuinely not base64 — callers report that, because a
 * blob that cannot be decoded is not the same thing as no filter at all.
 */
export function decodeFilterBlob(blob: string): Uint8Array<ArrayBuffer> {
  // Decoded from the canonical form, so a blob that decodes is by construction a blob
  // that canonicalizes to the same key.
  const standard = normalizeFilterBlob(blob).replaceAll('-', '+').replaceAll('_', '/')
  const padded = standard.padEnd(Math.ceil(standard.length / 4) * 4, '=')

  const binaryString = atob(padded)
  const bytes = new Uint8Array(binaryString.length)
  for (let i = 0; i < binaryString.length; i++) {
    bytes[i] = binaryString.charCodeAt(i)
  }

  return bytes
}

/**
 * The categories and values a saved filter selects, e.g. `{ unit: ['Unit A'] }`. Takes the stored
 * `blob&grouping` string, so it can describe a filter without applying it to the URL.
 */
export async function parseSavedFilter(savedFilter: string): Promise<Record<string, string[]>> {
  const [filterBlob] = savedFilter.split('&')
  return parseFilterFromUrlString(await decompressFilters(filterBlob))
}

export function getCompressedFilterParams(): string | null {
  const urlParams = new URLSearchParams(window.location.search)
  return urlParams.get(FILTER_QUERYSTRING_PARAM)
}

export function getDecompressedFilterParams(): Promise<{ urlParams: URLSearchParams; filters: string }> {
  const urlParams = new URLSearchParams(window.location.search)
  const compressedFilters = urlParams.get(FILTER_QUERYSTRING_PARAM)
  const compressedGrouping = urlParams.get(GROUPING_QUERYSTRING_PARAM)

  if (compressedGrouping) {
    setGrouping(compressedGrouping)
  }

  if (!compressedFilters) {
    return Promise.resolve({ urlParams, filters: '' })
  }

  return decompressFilters(compressedFilters).then((decompressedFilters) => ({
    urlParams,
    filters: decompressedFilters,
  }))
}

export function compressFiltersAndOpenUrlInNewWindow(url: URL, filterUrlString: string): Promise<void> {
  return compressFilters(filterUrlString).then((compressed) => {
    url.searchParams.set(FILTER_QUERYSTRING_PARAM, compressed)
    window.open(url, '_blank')
  })
}

/*
 * UNCOMPRESSED PASSTHROUGH
 *
 * `compressFilters` returns its input unchanged when `CompressionStream` is absent, and
 * `decompressFilters` does the same when `DecompressionStream` is absent.
 *
 * The reason is not browser support. `package.json`'s production browserslist floor is
 * `chrome >= 120, firefox >= 120, safari >= 18, edge >= 120`, and every one of those
 * ships both APIs (Chrome 80+, Firefox 113+, Safari 16.4+). On the stated support policy
 * the branch is unreachable in a browser.
 *
 * It survives for the tests, and only for the tests: it keeps the filter param readable,
 * so a suite can assert on `?filter=subgrid%3DALV` rather than an opaque base64 string.
 * What the old comment got wrong was crediting Jest as an *environment limitation* — that
 * framing let an entire suite exercise the passthrough while believing it covered the
 * encoding, which is why #4559, a bug wholly inside the base64 layer, had no failing test.
 *
 * A test that means to cover encoding must install the real Web APIs; see
 * `src/fixtures/filterBlob.fixtures.ts`.
 */

/**
 * Runs `data` through a compression transform and returns the output bytes.
 *
 * The write must not be awaited before the read starts. A `TransformStream` applies
 * backpressure from construction — its readable side has a high-water mark of 0 — so
 * `writer.write()` does not settle until something reads. Chromium implements that to the
 * letter, and awaiting the write first deadlocks: the promise never settles, so a filter
 * silently never applies and there is no error anywhere to notice. Node's
 * `CompressionStream` happens to use a non-zero mark and resolves regardless, which is why
 * a Jest test running on Node's implementation cannot see the deadlock at all.
 *
 * `Promise.all` starts both sides and attaches a handler to each immediately. That keeps a
 * failure on one side from surfacing as an unhandled rejection beside the one the caller
 * already handles — Sentry captures unhandled rejections too, so a single failure would be
 * reported twice.
 */
async function transformBytes(
  stream: CompressionStream | DecompressionStream,
  data: Uint8Array<ArrayBuffer>
): Promise<Uint8Array<ArrayBuffer>> {
  const writer = stream.writable.getWriter()
  const reader: ReadableStreamDefaultReader<Uint8Array> = stream.readable.getReader()

  const write = (async () => {
    await writer.write(data)
    await writer.close()
  })()

  const read = (async () => {
    const chunks: Uint8Array[] = []
    let done = false

    while (!done) {
      const { value, done: readerDone } = await reader.read()
      done = readerDone
      if (value) {
        chunks.push(value)
      }
    }

    return chunks
  })()

  const [, chunks] = await Promise.all([write, read])

  const totalLength = chunks.reduce((acc, chunk) => acc + chunk.length, 0)
  const combined = new Uint8Array(totalLength)
  let offset = 0
  for (const chunk of chunks) {
    combined.set(chunk, offset)
    offset += chunk.length
  }

  return combined
}

/**
 * Compresses a filter string using gzip compression and encodes as base64.
 * This helps keep URL parameters short when dealing with complex filter sets.
 *
 * @param filterString Filter string to compress
 * @returns Promise resolving to base64-encoded compressed string
 */
export async function compressFilters(filterString: string): Promise<string> {
  if (typeof CompressionStream === 'undefined') {
    return Promise.resolve(filterString) // Uncompressed passthrough — see UNCOMPRESSED PASSTHROUGH above.
  }

  // Convert filters array to JSON string and encode as bytes
  const encoder = new TextEncoder()
  const data = encoder.encode(filterString)

  const compressed = await transformBytes(new CompressionStream('gzip'), data)

  // Convert compressed data to base64 string for URL encoding
  const blob = new Blob([compressed])
  const arrayBuffer = await blob.arrayBuffer()
  const base64 = btoa(String.fromCharCode(...new Uint8Array(arrayBuffer)))
  return toUrlSafeBase64(base64)
}

/**
 * Decompresses a base64-encoded gzip string back to a filter string.
 *
 * Rejects rather than resolving to `''` when the blob cannot be decoded: an empty
 * result is indistinguishable from "no filter applied", so the caller cannot tell a
 * corrupt filter from an absent one and cannot report it.
 *
 * @param compressed Base64-encoded compressed filter data, either alphabet
 * @returns Promise resolving to decompressed filter string
 */
async function decompressFilters(compressed: string): Promise<string> {
  if (typeof DecompressionStream === 'undefined') {
    // Passthrough — see UNCOMPRESSED PASSTHROUGH above. Note the asymmetry: a param that
    // *is* a real blob is returned as its base64 text rather than rejected, so it parses
    // into nonsense filters instead of reporting. Pinned by test, not fixed here.
    return Promise.resolve(compressed)
  }

  const bytes = decodeFilterBlob(compressed)

  const decompressed = await transformBytes(new DecompressionStream('gzip'), bytes)

  // Convert decompressed data back to a filter string
  const blob = new Blob([decompressed])
  return await blob.text()
}
